import { useEffect, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@buildex/ui';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { $currentObject } from '@buildex/core';
import type { LaunchStep, ModuleManifest } from '@buildex/module-contract';
import {
  $launchStatus,
  $launchStatusError,
  $launchStatusLoading,
  launchStatusRequested,
  nextStep,
  stepAvailable,
  stepState,
  type LaunchStatus,
  type StepState,
} from './model';

const STEPS: readonly LaunchStep[] = [1, 2, 3];

/** Путь шага — из пункта меню модуля с launchStep (первый подходящий) */
function resolveStepPaths(manifests: ModuleManifest[]): Partial<Record<LaunchStep, string>> {
  const paths: Partial<Record<LaunchStep, string>> = {};
  for (const manifest of manifests) {
    for (const item of manifest.menuItems) {
      const step = item.launchStep;
      if (step !== undefined && paths[step] === undefined) paths[step] = item.path;
    }
  }
  return paths;
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

interface StepCardProps {
  step: LaunchStep;
  status: LaunchStatus | null;
  path: string | undefined;
  loading: boolean;
}

function StepCard({ step, status, path, loading }: StepCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const state: StepState | null = status === null ? null : stepState(status, step);
  const available = status !== null && stepAvailable(status, step);
  const clickable = available && path !== undefined;

  const color =
    state === 'done' ? 'teal' : state === 'partial' ? 'yellow' : available ? 'blue' : 'gray';

  let hint = '';
  if (status !== null) {
    if (state === 'done') hint = t('launch.done');
    else if (state === 'partial')
      hint = t('launch.partial', { done: status.pricedCount, total: status.sellableCount });
    else if (!available) hint = t('launch.locked');
    else hint = t('launch.pending');
    if (step === 1 && status.unitsCount > 0) hint = t('launch.units', { n: status.unitsCount });
  }

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={{
        opacity: status !== null && !available ? 0.55 : 1,
        borderColor: state === 'done' ? 'var(--mantine-color-teal-5)' : undefined,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ThemeIcon
            radius="xl"
            size={32}
            color={color}
            variant={state === 'done' ? 'filled' : 'light'}
          >
            {state === 'done' ? <CheckIcon /> : <Text fw={700}>{step}</Text>}
          </ThemeIcon>
          <div>
            <Text fw={600}>{t(`launch.step${step}.title`)}</Text>
            <Text size="xs" c="dimmed">
              {t(`launch.step${step}.desc`)}
            </Text>
            {loading && status === null ? (
              <Skeleton height={12} mt={8} width={120} />
            ) : (
              <Text size="xs" c={color} mt={6}>
                {hint}
              </Text>
            )}
          </div>
        </Group>
        {path === undefined ? (
          <Tooltip label={t('launch.noModule')}>
            <Badge color="gray" variant="light">
              —
            </Badge>
          </Tooltip>
        ) : (
          <Button
            size="xs"
            variant={state === 'done' ? 'default' : 'light'}
            disabled={!clickable}
            onClick={() => navigate(path)}
            style={{ flexShrink: 0 }}
          >
            {t('launch.open')}
          </Button>
        )}
      </Group>
    </Card>
  );
}

interface LaunchTrackProps {
  manifests: ModuleManifest[];
}

/** «Дорожка» запуска объекта: 3 шага, доступные последовательно, и CTA */
export function LaunchTrack({ manifests }: LaunchTrackProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { current, status, error, loading, refresh } = useUnit({
    current: $currentObject,
    status: $launchStatus,
    error: $launchStatusError,
    loading: $launchStatusLoading,
    refresh: launchStatusRequested,
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const paths = useMemo(() => resolveStepPaths(manifests), [manifests]);
  const validStatus =
    status !== null && current !== null && status.objectId === current.id ? status : null;
  const next = validStatus === null ? null : nextStep(validStatus);
  const ctaPath = next === null ? undefined : paths[next];
  const ctaLabel =
    validStatus === null || next === 1
      ? t('launch.cta.start')
      : next === null
        ? t('launch.cta.done')
        : t('launch.cta.continue');

  return (
    <Card withBorder radius="lg" padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Title order={4}>{t('launch.title')}</Title>
            {current === null ? (
              <Text size="sm" c="dimmed">
                {t('launch.noObject')}
              </Text>
            ) : (
              <Text size="sm" c="dimmed">
                {t('launch.current')}:{' '}
                <Text component="span" fw={600} c="var(--mantine-color-text)">
                  {current.name}
                </Text>
                {current.name !== current.id ? ` · ${current.id}` : ''}
              </Text>
            )}
          </div>
          {current !== null ? (
            <Group gap="xs">
              <Button variant="subtle" size="xs" onClick={() => refresh()} loading={loading}>
                {t('launch.refresh')}
              </Button>
              <Button
                size="sm"
                disabled={validStatus === null || next === null || ctaPath === undefined}
                onClick={() => ctaPath && navigate(ctaPath)}
              >
                {ctaLabel}
              </Button>
            </Group>
          ) : null}
        </Group>

        {error !== null ? (
          <Alert color="red" variant="light">
            {t('launch.error')}: {error}
          </Alert>
        ) : null}

        {current !== null ? (
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            {STEPS.map((step) => (
              <StepCard
                key={step}
                step={step}
                status={validStatus}
                path={paths[step]}
                loading={loading}
              />
            ))}
          </SimpleGrid>
        ) : null}
      </Stack>
    </Card>
  );
}
